import type { ReactElement } from 'react'

import type { Poem } from '@maria-cavalheiro-website/shared-types'
import {
  SITE_CONFIG,
  findCollection,
  formatPublishedDate,
} from '@maria-cavalheiro-website/shared-types/config'

import { PoemFigure } from '../scenes/poem-figure.tsx'
import { poemLeaves, type PoemLeaf } from './poem-leaves.ts'
import { StanzaLines } from './stanza-lines.tsx'

export interface PoemArticleProps {
  readonly poem: Poem
  /** Heading level for the poem title: 1 on its own page, 2 inside a listing. */
  readonly headingLevel: 1 | 2
  /**
   * Whether to draw the stanza figures.
   *
   * DEFAULTS TO TRUE, which is what the prerendered document gets: an illustrated
   * poem is the page as published, and the reader turns the pictures off from
   * there. `useIllustrations` owns the state; this component owns nothing.
   *
   * IT IS "ARE THEY IN THE DOCUMENT", NOT "CAN THE READER SEE THEM". The two part
   * company for one `--motion-duration` after the switch is released, while the
   * figures are fading out — `PoemPage` passes `mounted` here and `visible` to the
   * button, and the stylesheet does the rest.
   */
  readonly showIllustrations?: boolean
}

interface LeafProps {
  readonly leaf: PoemLeaf
  readonly showFigure: boolean
}

/**
 * One stanza, and the picture beside it on the two or three stanzas that carry one.
 *
 * THE STANZA IS STILL A `<p>` AND THE VERSE BREAKS ARE STILL `<br>`. The leaf
 * wrapper is a plain `<div>` around them, carrying layout and nothing else, so
 * the structure a screen reader announces is exactly what it was before the
 * spread existed: a paragraph per stanza, a line break per verse.
 *
 * THE FIGURE FOLLOWS THE STANZA IN DOCUMENT ORDER, ALWAYS — including on the
 * leaves that render right-to-left. `_poems.sass` reverses those visually with
 * `row-reverse`, which would be a § 1.3.2 Meaningful Sequence defect if the
 * reversed content carried meaning or focus. It carries neither: the figure is an
 * `<img alt="">`, out of the accessibility tree and unfocusable, so the sequence
 * both a screen reader and the tab ring see is stanza after stanza, unchanged.
 *
 * THE ART BOX RENDERS ONLY WHEN THERE IS ART, which since phase 0021 is most
 * stanzas' answer: `poemLeaves` picks two or three per poem rather than one per
 * stanza. An unillustrated leaf is a single-child flex row — the verse across the
 * whole spread, with no empty track held open beside it.
 *
 * A LEAF WITH ITS PICTURE TURNED OFF IS THE SAME CASE AS A LEAF THAT NEVER HAD
 * ONE, which is why `showFigure` collapses into the figure rather than adding a
 * second branch around it. The stanza keeps its class and its place either way, so
 * a poem read without pictures is the same document with less in it.
 */
function Leaf({ leaf, showFigure }: LeafProps): ReactElement {
  const figure = showFigure ? leaf.figure : undefined

  return (
    <div className={`poem__leaf poem__leaf--${leaf.side}`}>
      <p className="poem__stanza">
        <StanzaLines stanza={leaf.stanza} />
      </p>

      {figure === undefined ? null : (
        <div className="poem__leaf-art">
          <PoemFigure figure={figure} />
        </div>
      )}
    </div>
  )
}

/**
 * Pure presenter for a single poem.
 *
 * Stanzas are paragraphs and verse breaks are `<br>`: the structure a screen
 * reader announces matches the structure on the page. The design nested `<span>`s
 * instead, which says nothing about where a stanza begins. The heading level is a
 * prop so the surrounding page keeps a contiguous hierarchy with no skipped
 * intervals (rules/frontend.md § 5).
 *
 * PHASE 0018 GAVE IT THE PICTURES. Each stanza became a leaf that may carry a
 * figure beside it, in the flow — where there had been one fixed mark in the
 * viewport margin that never moved with the verse and was, on a phone, a watermark
 * at 0.11 opacity, which is to say, for most readers, nothing at all.
 *
 * PHASE 0021 TOOK MOST OF THEM BACK OUT. One figure per stanza meant the verse
 * never got a plain line: every stanza had an icon at its shoulder, and at six or
 * seven of them the column of drawings read as decoration rather than as
 * illustration. `poemLeaves` now picks two or three stanzas per poem — the first,
 * the last, and one in between — and the rest of the verse stands on its own. The
 * title plate went with them, for the reason given over the header below.
 *
 * AND THE READER CAN TURN THEM OFF. `showIllustrations` UNMOUNTS the figures; it
 * does not hide them in CSS. A `display: none` image is still fetched and still
 * occupies the DOM, so a reader who has said "just the verse" would pay for the
 * files anyway and a crawler would still see them. Unmounting is the only reading
 * of "hide" that costs what the reader expects it to cost.
 *
 * NOTHING ELSE MOVES WHEN THEY GO. The leaves keep their classes and their
 * alternation, and `.poem__leaf-art` is the only box that leaves the flow — the
 * stanza simply takes the width the picture was using.
 */
export function PoemArticle({
  poem,
  headingLevel,
  showIllustrations = true,
}: PoemArticleProps): ReactElement {
  const Heading = headingLevel === 1 ? 'h1' : 'h2'
  const publishedLabel = formatPublishedDate(poem.publishedAt)
  const collection = findCollection(poem.collection)
  const legend = [collection?.label, poem.theme].filter(Boolean).join(' · ')

  return (
    <article className="poem" aria-labelledby={`poema-${poem.slug}`}>
      {/* A plain div, not <header>: the article is already named by aria-labelledby,
          and a nested <header> makes landmark identity ambiguous to tooling. */}
      {/* NO TITLE PLATE. Phase 0018 opened the poem with a composed emblem above
          its name; phase 0021 took it out. With the stanza pictures reduced to two
          or three, the plate was a fourth picture in the one place a reader has no
          verse to read it against — it pushed the title down a screen on a phone
          and said, in pictures, what the title says in words. `PoemPlate` lives on
          for the book reader, where a page IS a cover. */}
      <div className="poem__header">
        <p className="poem__legend">{legend}</p>

        <Heading className="poem__title" id={`poema-${poem.slug}`}>
          {poem.title}
        </Heading>

        {/* Two poems carry no date at all. The whole element goes rather than
            rendering an empty `datetime`, which is invalid HTML and a false SEO
            signal — the design emitted an empty string and a stray block. */}
        {publishedLabel === undefined ? null : (
          <p className="poem__meta">
            <time className="poem__date" dateTime={poem.publishedAt}>
              {publishedLabel}
            </time>
          </p>
        )}
      </div>

      <div className="poem__body">
        {poemLeaves(poem).map((leaf, index) => (
          <Leaf
            key={`${poem.slug}-estrofe-${String(index)}`}
            leaf={leaf}
            showFigure={showIllustrations}
          />
        ))}
      </div>

      {/* A <p>, not a <footer>: the poem is not a sectioning root, and a nested
          footer muddies the landmark tree — the same reasoning as the header above. */}
      <p className="poem__signature">{SITE_CONFIG.author}</p>
    </article>
  )
}
